import { notify } from '@billy/design-system'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { getOrganizationViewUrl } from '../../../../../utils/routing/getOrganizationViewUrl'
import { useDeleteFolder } from '../../../hooks/useDeleteFolder'
import { useFetchFolderTree } from '../../../hooks/useFetchFolderTree'
import { useIsClientDocumentsRoute } from '../../../hooks/useIsClientDocumentsRoute'
import { getDocumentsRoutes } from '../../../utils/getDocumentsRoutes'
import { slugifyFolderName } from '../../../utils/slugifyFolderName'
import { FolderContentParams } from '../types/folderContentParams'

interface UseDeleteCurrentFolderProps {
  onSuccess?: () => void
}

export const useDeleteCurrentFolder = (props?: UseDeleteCurrentFolderProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { organization } = useUserOrganization()
  const { tree } = useFetchFolderTree()
  const isClientDocumentsRoute = useIsClientDocumentsRoute()
  const documentsRoutes = getDocumentsRoutes(isClientDocumentsRoute)
  const { folderId } = useParams<FolderContentParams>() as FolderContentParams

  const currentNode = tree?.find((node) => node.id === folderId)
  const parentId = currentNode?.parentId ?? null
  const folderName = currentNode?.name ?? ''

  const { deleteFolder, isProcessing } = useDeleteFolder({
    onSuccess: () => {
      notify({
        id: NotificationKeys.DocumentsDeleteFolder,
        message: t('documents.delete_folder.notification.success', { name: folderName }),
        variant: 'success',
      })
      props?.onSuccess?.()

      if (!organization) {
        return
      }

      if (parentId) {
        const parentNode = tree?.find((node) => node.id === parentId)
        const slug = parentNode ? slugifyFolderName(parentNode.name) : undefined
        const route = documentsRoutes.getFolderRoute(parentId, slug)
        navigate(getOrganizationViewUrl(organization.url, route))
      } else {
        navigate(getOrganizationViewUrl(organization.url, documentsRoutes.getMainRoute()))
      }
    },
    onError: () => {
      notify({
        id: NotificationKeys.DocumentsDeleteFolder,
        message: t('documents.delete_folder.notification.error'),
        variant: 'error',
      })
    },
  })

  const deleteCurrentFolder = useCallback(() => {
    if (organization) {
      deleteFolder({ organizationId: organization.id, folderId })
    }
  }, [deleteFolder, organization, folderId])

  return useMemo(() => ({ deleteCurrentFolder, isProcessing }), [deleteCurrentFolder, isProcessing])
}
